import React from 'react';
import { isObject, isString, isNumber } from '../helpers';
import {
  isAttachment,
  isTranslation,
  isAccreditation,
  isExtra,
  isTheme,
  isSegment,
  isTransfer,
  isExtraWithDependencies,
  isProgramSuggestedOption,
  isProgramIncludedOption,
  isProgramAlternativeHotel,
  isProgram,
  isPoi,
  isPeriod,
} from '../typeCheckers';
import { getResolvedAttachment } from '../labelGetters';
import AttachmentDisplay from './AttachmentDisplay';
import TranslationDisplay from './TranslationDisplay';
import AccreditationDisplay from './AccreditationDisplay';
import ExtraDisplay from './ExtraDisplay';
import ThemeDisplay from './ThemeDisplay';
import SegmentDisplay from './SegmentDisplay';
import TransferDisplay from './TransferDisplay';
import ExtraWithDependenciesDisplay from './ExtraWithDependenciesDisplay';
import PoiDisplay from './PoiDisplay';
import PeriodDisplay from './PeriodDisplay';
import {
  ProgramSuggestedOptionDisplay,
  ProgramIncludedOptionDisplay,
  ProgramAlternativeHotelDisplay,
  ProgramExperienceDisplay,
  ProgramDisplay,
} from './ProgramDisplays';
import ObjectValueDisplay from './ObjectValueDisplay';
import ValueRenderer from './ValueRenderer';

export default function SmartItemDisplay({
  item,
  className = '',
  isNew = false,
  isModified = false,
  isRemoved = false,
  beforeKind,
  attachmentLabels,
  labels,
  fieldName,
}) {
  if (isAttachment(item)) {
    const resolvedAttachment = getResolvedAttachment(item, attachmentLabels);
    return (
      <AttachmentDisplay
        attachment={item}
        isNew={isNew}
        isModified={isModified}
        isRemoved={isRemoved}
        beforeKind={beforeKind}
        resolvedAttachment={resolvedAttachment}
      />
    );
  }
  if (isTranslation(item)) {
    return <TranslationDisplay translation={item} isNew={isNew} />;
  }
  if (isAccreditation(item, labels)) {
    return (
      <AccreditationDisplay
        accreditation={item}
        labels={labels}
        isNew={isNew}
      />
    );
  }
  if (isExtra(item, labels)) {
    return <ExtraDisplay extra={item} labels={labels} isNew={isNew} />;
  }
  if (isTheme(item, labels)) {
    return <ThemeDisplay theme={item} labels={labels} isNew={isNew} />;
  }
  if (isSegment(item, labels)) {
    return <SegmentDisplay segment={item} labels={labels} isNew={isNew} />;
  }
  if (isTransfer(item, labels)) {
    return <TransferDisplay transfer={item} labels={labels} isNew={isNew} />;
  }
  if (isExtraWithDependencies(item, labels)) {
    return (
      <ExtraWithDependenciesDisplay
        extraWithDeps={item}
        labels={labels}
        isNew={isNew}
      />
    );
  }
  if (isProgramSuggestedOption(item)) {
    return (
      <ProgramSuggestedOptionDisplay
        option={item}
        isNew={isNew}
        labels={labels}
      />
    );
  }
  // Use ProgramExperienceDisplay for programExperiences field
  if (fieldName === 'programExperiences' && isProgramIncludedOption(item)) {
    return (
      <ProgramExperienceDisplay
        experience={item}
        isNew={isNew}
        labels={labels}
      />
    );
  }
  if (isProgramIncludedOption(item)) {
    return (
      <ProgramIncludedOptionDisplay
        option={item}
        isNew={isNew}
        labels={labels}
      />
    );
  }
  if (isProgramAlternativeHotel(item)) {
    return (
      <ProgramAlternativeHotelDisplay
        hotel={item}
        isNew={isNew}
        labels={labels}
      />
    );
  }
  if (isProgram(item)) {
    return (
      <ProgramDisplay
        program={item}
        labels={labels}
        isNew={isNew}
        attachmentLabels={attachmentLabels}
      />
    );
  }
  if (isPoi(item)) {
    return <PoiDisplay poi={item} isNew={isNew} />;
  }
  if (isPeriod(item)) {
    return <PeriodDisplay period={item} isNew={isNew} />;
  }
  if (isObject(item)) {
    return (
      <ObjectValueDisplay
        value={item}
        isNew={isNew}
        attachmentLabels={attachmentLabels}
        labels={labels}
      />
    );
  }
  // For primitives like guiding language codes, display uppercase if it's a language field
  if (fieldName === 'guidingLanguages' && (isString(item) || isNumber(item))) {
    return <span className={className}>{String(item).toUpperCase()}</span>;
  }
  return (
    <span className={className}>
      <ValueRenderer value={item} />
    </span>
  );
}
